import "isomorphic-fetch";
import { normalize } from "normalizr";
import { camelizeKeys } from "humps";
import { portalConfig } from "../lib/portal";
import dataToPortalQueryString from "../lib/dataToPortalQueryString";

const MAX_DATA_AGE = 12 * 60 * 60; // 12 hours

function fetchData(apiToken, url, schema = null) {
  const fetchConfig = {
    headers: { Authorization: `Token ${apiToken}` },
    method: "GET"
  };

  return fetch(url, fetchConfig).then((response) =>
    response.json().then((json) => {
      if (!response.ok) {
        return Promise.reject(json);
      }

      const camelizedJson = camelizeKeys(json);

      if (schema) {
        return Object.assign({}, normalize(camelizedJson, schema));
      } else {
        return camelizedJson;
      }
    })
  );
}

function dataAge(dataStatus) {
  if (dataStatus.lastUpdated === undefined) {
    return 999999999999;
  } else {
    return (Date.now() - dataStatus.lastUpdated) / 1000;
  }
}

function dataFetchingTime(dataStatus) {
  if (dataStatus.startFetching === undefined) {
    return 999999999999;
  } else {
    return (Date.now() - dataStatus.startFetching) / 1000;
  }
}

function needToRefreshEntityIds(dataStatus) {
  return dataStatus.didInvalidate || dataAge(dataStatus) > MAX_DATA_AGE;
}

function needToRefreshEntities(dataStatus) {
  const maxFetchingTime = 60; // 60 seconds
  const isFetching =
    dataStatus.isFetching && dataFetchingTime(dataStatus) < maxFetchingTime;

  return (
    (dataStatus.didInvalidate || dataAge(dataStatus) > MAX_DATA_AGE) &&
    !isFetching
  );
}

function needToRefreshOfferTypeCounts(dataStatus) {
  return dataStatus.didInvalidate || dataAge(dataStatus) > MAX_DATA_AGE;
}

const getEntity = (clientConfig, entityId, path, schema = null) => {
  const portal = portalConfig(clientConfig.portal);
  const apiToken = clientConfig.token;
  const fullUrl = `${portal.apiHost}${path}/${entityId}`;

  return fetchData(apiToken, fullUrl, schema);
};

const getEntityIds = (
  clientConfig,
  dataStatus,
  path,
  offerType,
  limits = {},
  filter = {}
) => {
  const refresh = needToRefreshEntityIds(dataStatus);

  if (refresh) {
    const portal = portalConfig(clientConfig.portal);
    const apiToken = clientConfig.token;
    const fullUrl = `${
      portal.apiHost
    }${path}?ids_only=true&${dataToPortalQueryString(
      limits,
      filter,
      offerType
    )}`;

    return fetchData(apiToken, fullUrl);
  } else {
    return Promise.resolve();
  }
};

const getEntities = (
  clientConfig,
  dataStatus,
  path,
  schema = null,
  options = {},
  limits = {},
  filter = {}
) => {
  const refresh = needToRefreshEntities(dataStatus);

  if (refresh) {
    const portal = portalConfig(clientConfig.portal);
    const apiToken = clientConfig.token;
    const withDetails = !!options.withDetails;
    const excludeNonDefaultOfferExtras =
      withDetails && filter["offerExtraId"].length === 0;
    const excludeInverseOfferExtraId =
      limits["offerExtraId"] === undefined ||
      limits["offerExtraId"].length === 0;
    const includeDefaultOfferExtrasWithoutFrontendFilter =
      limits["offerExtraId"] === undefined ||
      limits["offerExtraId"].length === 0;
    const productType = options.productType || "";
    const fullUrl = `${
      portal.apiHost
    }${path}?with_details=${withDetails}&product_type=${productType}&exclude_non_default_offer_extras=${excludeNonDefaultOfferExtras}&exclude_inverse_offer_extra_id=${excludeInverseOfferExtraId}&include_default_offer_extras_without_frontend_filter=${includeDefaultOfferExtrasWithoutFrontendFilter}&${dataToPortalQueryString(
      limits,
      filter,
      options.offerType
    )}`;

    return fetchData(apiToken, fullUrl, schema);
  } else {
    return Promise.resolve();
  }
};

const getCount = (clientConfig, dataStatus, path, filter = {}) => {
  const refresh = needToRefreshOfferTypeCounts(dataStatus);

  if (refresh) {
    const portal = portalConfig(clientConfig.portal);
    const apiToken = clientConfig.token;
    const limits = clientConfig.limits;
    const fullUrl = `${portal.apiHost}${path}?${dataToPortalQueryString(
      limits,
      filter
    )}`;

    return fetchData(apiToken, fullUrl);
  } else {
    return Promise.resolve();
  }
};

export { getEntity, getEntityIds, getEntities, getCount };
